import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  AlertTriangle,
  ArrowRight,
  Phone,
  Search,
  Heart,
  Brain,
  Users,
  Activity,
  Clock,
  Info,
  CheckCircle
} from "lucide-react";
import { Header, Footer } from "../../../components/Layout";

const MarihuanaPage = () => {
  const myths = [
    { 
      myth: "La marihuana no es adictiva", 
      reality: "Aproximadamente el 9% de los usuarios desarrollan dependencia. En adolescentes, la cifra sube al 17%.",
      status: "falso"
    },
    { 
      myth: "Es 100% natural y segura", 
      reality: "Natural no significa seguro. El THC de hoy es mucho más potente que hace décadas, aumentando riesgos.",
      status: "engañoso"
    },
    { 
      myth: "No tiene síndrome de abstinencia", 
      reality: "Los usuarios frecuentes experimentan irritabilidad, insomnio, ansiedad y pérdida de apetito al dejar.",
      status: "falso"
    },
    { 
      myth: "Mejora la creatividad y rendimiento", 
      reality: "Puede dar sensación de creatividad, pero estudios muestran deterioro en memoria y funciones ejecutivas.",
      status: "engañoso"
    }
  ];

  const symptoms = [
    { title: "Desmotivación crónica", description: "Pérdida de interés en actividades, metas y responsabilidades (síndrome amotivacional)" },
    { title: "Problemas de memoria", description: "Dificultad para recordar información reciente y aprender cosas nuevas" },
    { title: "Aislamiento social", description: "Preferencia por estar solo o solo con otros consumidores" },
    { title: "Tolerancia creciente", description: "Necesidad de consumir más cantidad o productos más potentes" },
    { title: "Uso como escape", description: "Recurrir a la marihuana ante cualquier emoción negativa o estrés" },
    { title: "Deterioro académico/laboral", description: "Bajo rendimiento, ausencias frecuentes, pérdida de oportunidades" }
  ];

  const riskFactors = [
    { group: "Adolescentes", risk: "Alto", reason: "El cerebro en desarrollo es especialmente vulnerable. Mayor riesgo de psicosis y adicción." },
    { group: "Antecedentes de salud mental", risk: "Alto", reason: "Puede desencadenar o empeorar esquizofrenia, ansiedad y depresión." },
    { group: "Uso diario", risk: "Alto", reason: "El consumo frecuente multiplica significativamente el riesgo de dependencia." },
    { group: "Uso recreativo ocasional", risk: "Moderado", reason: "Menor riesgo, pero no nulo. Puede escalar con el tiempo." }
  ];

  const treatmentOptions = [
    {
      title: "Terapia Cognitivo-Conductual",
      description: "El tratamiento más efectivo para dependencia a cannabis. Trabaja motivación, triggers y hábitos.",
      duration: "6-12 meses",
      icon: Brain
    },
    {
      title: "Terapia Motivacional",
      description: "Especialmente útil cuando hay ambivalencia sobre dejar de consumir. Fortalece la motivación al cambio.",
      duration: "4-8 sesiones iniciales",
      icon: Activity
    },
    {
      title: "Tratamiento Ambulatorio",
      description: "La mayoría de casos de dependencia a marihuana se tratan sin internación. Desde $350.000/mes.",
      duration: "6-12 meses + seguimiento",
      icon: Clock
    },
    {
      title: "Grupos de Apoyo (MA)",
      description: "Marihuana Anónimos ofrece apoyo continuo basado en los 12 pasos. Gratuito y confidencial.",
      duration: "Continuo",
      icon: Users
    }
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalCondition",
    "name": "Dependencia a Cannabis",
    "alternateName": ["Adicción a marihuana", "Trastorno por uso de cannabis"],
    "description": "La dependencia a marihuana es una condición real que afecta especialmente a usuarios frecuentes y adolescentes.",
    "signOrSymptom": symptoms.map(s => ({
      "@type": "MedicalSignOrSymptom",
      "name": s.title
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Adicciones", "item": "https://sinadicciones.org/adicciones" },
      { "@type": "ListItem", "position": 3, "name": "Marihuana", "item": "https://sinadicciones.org/adicciones/marihuana" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Dependencia a Marihuana: Mitos, Síntomas y Tratamiento | Sin Adicciones</title>
        <meta name="description" content="La verdad sobre la adicción a marihuana. Desmitificamos creencias, explicamos síntomas de dependencia y opciones de tratamiento en Chile." />
        <meta property="og:title" content="Marihuana: Mitos, Síntomas y Tratamiento" />
        <meta property="og:description" content="¿Es adictiva la marihuana? Conoce la verdad sobre la dependencia a cannabis, señales de alerta y cómo buscar ayuda." />
        <link rel="canonical" href="https://sinadicciones.org/adicciones/marihuana" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/adicciones/marihuana" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/adicciones" className="hover:text-emerald-400 transition-colors">
              Adicciones
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Marihuana</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-green-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-5xl">🌿</span>
              <div className="px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full">
                <span className="text-green-400 font-medium">Desmitificando creencias</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Marihuana:{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                ¿Es realmente adictiva?
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              Aunque muchos la consideran "inofensiva", la marihuana puede generar dependencia real, 
              especialmente en adolescentes y usuarios frecuentes. Conoce los hechos basados en evidencia.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/herramientas/dast"
                className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Activity className="w-5 h-5" />
                Test de Evaluación
              </Link>
              <Link 
                to="/profesionales"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Buscar Profesional
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Myths vs Reality */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Mitos vs Realidad
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Hay mucha desinformación sobre la marihuana. Estos son los hechos según la ciencia actual.
            </p>
          </div>

          <div className="space-y-6">
            {myths.map((item, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50"
              >
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        item.status === 'falso' 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {item.status.toUpperCase()}
                      </span>
                      <h3 className="text-lg font-bold text-white">"{item.myth}"</h3>
                    </div>
                    <div className="flex items-start gap-3 pl-4 border-l-2 border-emerald-500/50">
                      <Info className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <p className="text-slate-300">{item.reality}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Groups */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Grupos de Mayor Riesgo
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              No todos tienen el mismo riesgo de desarrollar dependencia. Estos factores aumentan la vulnerabilidad.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {riskFactors.map((item, index) => (
              <div 
                key={index}
                className={`p-6 rounded-2xl border ${
                  item.risk === 'Alto' 
                    ? 'bg-red-500/10 border-red-500/30' 
                    : 'bg-yellow-500/10 border-yellow-500/30'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold text-white">{item.group}</h3>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                    item.risk === 'Alto' 
                      ? 'bg-red-500/20 text-red-400' 
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    Riesgo {item.risk}
                  </span>
                </div>
                <p className="text-slate-300">{item.reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Symptoms */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Señales de Dependencia
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              La dependencia a marihuana se desarrolla gradualmente. Estas son las señales más comunes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {symptoms.map((symptom, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-green-500/20"
              >
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">{symptom.title}</h3>
                    <p className="text-slate-400 text-sm">{symptom.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link 
              to="/herramientas/dast"
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl transition-all"
            >
              Evaluar mi Situación
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Treatment */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Opciones de Tratamiento
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              La buena noticia: la dependencia a marihuana responde bien al tratamiento ambulatorio con terapia especializada.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {treatmentOptions.map((option, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50 hover:border-emerald-500/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <option.icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold text-white">{option.title}</h3>
                      <span className="text-emerald-400 text-sm">{option.duration}</span>
                    </div>
                    <p className="text-slate-400">{option.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link 
              to="/profesionales"
              className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
            >
              <Search className="w-5 h-5" />
              Buscar Profesional
            </Link>
            <Link 
              to="/tratamiento-ambulatorio"
              className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-xl transition-all"
            >
              Sobre Tratamiento Ambulatorio
            </Link>
          </div>
        </div>
      </section>

      {/* Key Facts */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-green-900/30 to-slate-800/50 rounded-3xl p-8 md:p-12 border border-green-500/20">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
              Datos Clave sobre Cannabis
            </h2>
            
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-green-400 mb-2">9%</p>
                <p className="text-slate-400">de usuarios desarrollan dependencia</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-green-400 mb-2">17%</p>
                <p className="text-slate-400">en adolescentes que inician temprano</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-green-400 mb-2">3x</p>
                <p className="text-slate-400">más THC que hace 30 años</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Parents */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-slate-800/50 rounded-3xl p-8 md:p-12 border border-slate-700/50">
            <div className="flex items-center gap-3 mb-6">
              <Users className="w-8 h-8 text-emerald-400" />
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Para Padres y Familias
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Señales en adolescentes:</h3>
                <ul className="space-y-3">
                  {[
                    "Cambio repentino de amigos",
                    "Bajo rendimiento escolar",
                    "Ojos rojos frecuentes",
                    "Olor característico en ropa",
                    "Cambios de humor bruscos",
                    "Aislamiento del núcleo familiar"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Qué hacer:</h3>
                <ul className="space-y-3">
                  {[
                    "Mantener comunicación abierta sin juzgar",
                    "Informarse sobre los riesgos reales",
                    "Buscar ayuda profesional temprana",
                    "No minimizar el consumo",
                    "Establecer límites claros con amor",
                    "Considerar terapia familiar"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-300">
                      <ArrowRight className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900/50 to-slate-800/50 border border-emerald-500/20 p-8 md:p-12 text-center">
            <Heart className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              ¿Preocupado por tu consumo?
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Reconocer que puede haber un problema es valiente. 
              Habla con un profesional y evalúa tu situación de forma confidencial.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/asesoria"
                className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Asesoría Experta
              </Link>
              <Link 
                to="/herramientas"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700"
              >
                Tests de Autoevaluación
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Contenido Relacionado</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Adicciones Conductuales", path: "/adicciones/conductuales", icon: "📱" },
              { title: "Tratamiento Ambulatorio", path: "/tratamiento-ambulatorio", icon: "🏥" },
              { title: "Terapia Cognitivo-Conductual", path: "/tratamiento-cognitivo-conductual", icon: "🧠" },
              { title: "Herramientas de Evaluación", path: "/herramientas", icon: "📋" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-emerald-500/30 transition-all group"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-white group-hover:text-emerald-400 transition-colors">{item.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MarihuanaPage;
